import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { HelpPanel } from "./HelpPanel";

export interface HelpTarget {
  /** Open this article. */
  slug?: string;
  /** Open the article that answers this help key (`HELP_KEYS`). */
  key?: string;
}

export interface HelpApi {
  isOpen: boolean;
  /** Opens the help panel: on the article list, or on the article for `target`. */
  open: (target?: HelpTarget) => void;
  close: () => void;
}

const HelpContext = createContext<HelpApi | null>(null);

/** The help panel's controls, or `null` outside a `HelpProvider` (so a page can hide its help link). */
export function useHelp(): HelpApi | null {
  return useContext(HelpContext);
}

export function HelpProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ target: HelpTarget; session: number } | null>(null);
  const opener = useRef<HTMLElement | null>(null);

  const open = useCallback((target: HelpTarget = {}) => {
    const active = document.activeElement;
    if (!opener.current && active instanceof HTMLElement && active !== document.body) opener.current = active;
    // a new session each time, so the panel starts from the list or the asked article
    setState((s) => ({ target, session: (s?.session ?? 0) + 1 }));
  }, []);

  const close = useCallback(() => setState(null), []);

  // Focus goes back once the dialog is gone: while it is open the page behind is inert.
  const isOpen = state !== null;
  useEffect(() => {
    if (isOpen) return;
    const el = opener.current;
    opener.current = null;
    if (el && document.contains(el)) el.focus();
  }, [isOpen]);

  const api = useMemo<HelpApi>(() => ({ isOpen, open, close }), [isOpen, open, close]);

  return (
    <HelpContext.Provider value={api}>
      {children}
      {state && <HelpPanel key={state.session} target={state.target} onClose={close} />}
    </HelpContext.Provider>
  );
}
